import { useMemo, useState, type ChangeEvent } from 'react';
import { TERMINAL_PARAM_EXPORT_FORMAT, TerminalParamExportDocument } from '@reform-lite/contracts';
import { useT } from '@/i18n/use-t';
import { Button } from '@/components/ui/button';
import { extractApiError } from '@/lib/api-error';
import {
  useApplyImport,
  useImportPreview,
  useTerminalParamRegistry,
  type ImportApplyResult,
  type ImportPreview,
} from '@/hooks/use-terminal-params';
import {
  applyExpectations,
  ImportPreviewTable,
  ImportTotals,
  keptRows,
} from './import-preview-table';
import { TIMING_STATEMENT } from './parameter-editor';

/**
 * Preview-then-apply for a parameter file (RL-283, TP-FR-030), the same
 * shape the CSV importer uses: nothing is written until the operator has
 * seen what will be added, what will be changed and what will be left
 * alone. Conflicts are resolved by being shown — a changed row carries both
 * values and a checkbox — never by an overwrite-everything or a
 * skip-everything mode. Skipped rows show the registry's reason (TP-FR-031)
 * and cannot be ticked.
 */

type ParsedFile = { document: TerminalParamExportDocument } | { error: string };

/** Parse and validate a chosen file as the export contract, before any request. */
export function parseImportFile(text: string): ParsedFile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { error: 'This file is not valid JSON.' };
  }
  const parsed = TerminalParamExportDocument.safeParse(raw);
  if (!parsed.success) {
    const format = (raw as { format?: unknown } | null)?.format;
    if (format !== TERMINAL_PARAM_EXPORT_FORMAT) {
      return {
        error: `This is not a parameter export (expected format "${TERMINAL_PARAM_EXPORT_FORMAT}").`,
      };
    }
    const issue = parsed.error.issues[0];
    return {
      error:
        `The file does not match the export contract: ${issue?.path.join('.') ?? ''} ${issue?.message ?? ''}`.trim(),
    };
  }
  return { document: parsed.data };
}

export function ImportPanel({ onClose }: { onClose: () => void }) {
  const t = useT();
  const registry = useTerminalParamRegistry();
  const previewImport = useImportPreview();
  const applyImport = useApplyImport();
  const [document, setDocument] = useState<TerminalParamExportDocument | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const descriptors = useMemo(
    () => new Map((registry.data?.descriptors ?? []).map((d) => [d.key, d] as const)),
    [registry.data],
  );

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    setError(null);
    setPreview(null);
    setResult(null);
    setExcluded(new Set());
    const reader = new FileReader();
    reader.onerror = () => setError(t('parameters.import.readError', 'Could not read the file.'));
    reader.onload = async () => {
      const parsed = parseImportFile(String(reader.result ?? ''));
      if ('error' in parsed) {
        setDocument(null);
        setError(parsed.error);
        return;
      }
      setDocument(parsed.document);
      try {
        setPreview(await previewImport.mutateAsync(parsed.document));
      } catch (cause) {
        setError(extractApiError(cause));
      }
    };
    reader.readAsText(file);
  };

  const kept = keptRows(preview, excluded);

  const apply = async () => {
    if (document === null || preview === null) return;
    setError(null);
    try {
      const applied = await applyImport.mutateAsync({
        // Keep the source document intact. A cross-tenant preview replaces
        // tenant-local source ids with the resolved local target ids, so it
        // cannot safely be used to filter the source assignments by address.
        // expectedRows is the reviewed, selected subset.
        document,
        expectedRows: applyExpectations(kept),
      });
      setResult(applied);
      setPreview(null);
    } catch (cause) {
      setError(extractApiError(cause));
    }
  };

  return (
    <section
      className="space-y-4 rounded-lg border bg-card p-5"
      aria-label={t('parameters.import.label', 'Import parameters')}
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">
            {t('parameters.import.title', 'Import parameters from JSON')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t(
              'parameters.import.intro',
              'Choose a file exported from Back Office. Every row is checked against this registry first; nothing is written until you apply.',
            )}{' '}
            {t('parameters.editor.timing', TIMING_STATEMENT)}
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t('common.close', 'Close')}
        </Button>
      </header>

      <input
        type="file"
        accept=".json,application/json"
        aria-label={t('parameters.import.fileLabel', 'Parameter export file')}
        disabled={previewImport.isPending || applyImport.isPending}
        onChange={onFile}
      />

      {previewImport.isPending && (
        <p className="text-sm text-muted-foreground">
          {t('parameters.import.checking', 'Checking the file against this registry…')}
        </p>
      )}
      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {preview !== null && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('parameters.import.fromTenant', 'From tenant')}{' '}
            <span className="font-mono">{preview.source.tenant.slug}</span>
            {t('parameters.import.registryVersionLabel', ', registry v')}
            {preview.source.registryVersion}
            {t('parameters.import.exportedAt', ', exported')}{' '}
            {new Date(preview.source.exportedAt).toLocaleString()} ·{' '}
            {t('parameters.import.thisRegistryIs', 'this registry is v')}
            {preview.targetRegistryVersion}.
          </p>
          <ImportTotals totals={preview.totals} />
          <ImportPreviewTable
            preview={preview}
            descriptors={descriptors}
            excluded={excluded}
            onToggle={(address, included) =>
              setExcluded((current) => {
                const next = new Set(current);
                if (included) next.delete(address);
                else next.add(address);
                return next;
              })
            }
          />
          <div className="flex items-center gap-3">
            <Button
              type="button"
              disabled={kept.length === 0 || applyImport.isPending}
              onClick={() => void apply()}
            >
              {applyImport.isPending
                ? t('parameters.import.applying', 'Applying…')
                : `${t('parameters.import.apply', 'Apply')} ${kept.length} ${
                    kept.length === 1
                      ? t('parameters.import.change', 'change')
                      : t('parameters.import.changes', 'changes')
                  }`}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t(
                'parameters.import.leftAlone',
                'Unticked, unchanged and skipped rows are left alone.',
              )}
            </span>
          </div>
        </div>
      )}

      {result !== null && (
        <p className="text-sm" role="status">
          {t('parameters.import.applied', 'Applied')} {result.applied}{' '}
          {result.applied === 1
            ? t('parameters.import.change', 'change')
            : t('parameters.import.changes', 'changes')}
          .{' '}
          {result.totals.skip > 0 &&
            `${result.totals.skip} ${t('parameters.import.skipped', 'skipped.')} `}
          {t('parameters.editor.timing', TIMING_STATEMENT)}
        </p>
      )}
    </section>
  );
}
